import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import {
  Card,
  CardHeader,
  CardBody,
  Container,
  Row,
  Col,
  Button,
  Table,
  Input,
  InputGroup,
  InputGroupAddon,
  FormGroup,
  Label,
  Badge,
  Pagination,
  PaginationItem,
  PaginationLink,
  UncontrolledTooltip
} from "reactstrap";
import { ClipLoader } from "react-spinners";
import moment from "moment";
import NotificationAlert from "react-notification-alert";
import SimpleHeader from "../../components/Headers/SimpleHeader.jsx";
import api from "../../Api.js";
import LogDetails from "./modal/logs/LogDetails.jsx";

const SystemLogs = ({ translate, permission }) => {
  const [logs, setLogs] = useState([]);
  const [filteredLogs, setFilteredLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchLoading, setSearchLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [dateFilter, setDateFilter] = useState("");
  const [actionFilter, setActionFilter] = useState("");
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);
  const [page, setPage] = useState(1);
  const [totalLogs, setTotalLogs] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [uniqueDates, setUniqueDates] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [dateInputType, setDateInputType] = useState("select");   
  const logsPerPage = 15;

  const notificationAlertRef = React.useRef(null);

  const getPageNumbers = () => {
    const total = isSearching ? filteredTotalPages : totalPages;
    const current = page;
    if (total <= 10) {
      return Array.from({ length: total }, (_, i) => i + 1);
    }
    const pages = [1];
    if (current > 4) pages.push('start-ellipsis');
    const start = Math.max(2, current - 2);
    const end = Math.min(total - 1, current + 2);
    for (let i = start; i <= end; i++) pages.push(i);
    if (current < total - 3) pages.push('end-ellipsis');
    pages.push(total);
    return pages;
  };

  const getFormattedDates = (dates) => {
    return [...new Set(
      dates.map(item => moment(item).format('YYYY-MM-DD'))
    )].sort().reverse();
  };

  const loadUserUpdateHistory = async () => {
    setLoading(true);
    try {
      // Buscar todos os registros de histórico de atualização de usuários
      const response = await api.getUserUpdateHistoryAll();

      if (response && response.data) {
        const allHistory = Array.isArray(response.data) ? response.data : [];
        
        // Coletamos datas únicas para o filtro usando a função formatadora
        const dates = getFormattedDates(
          allHistory.map(item => item.updated_at)
        );
        
        setUniqueDates(dates);
        setLogs(allHistory);
        setTotalLogs(allHistory.length);
        setTotalPages(Math.ceil(allHistory.length / logsPerPage));
      }
    } catch (error) {
      console.error("Erro ao carregar histórico de atualizações:", error);
      if (notificationAlertRef.current) {
        const options = {
          place: "tr",
          message: (
            <div className="alert-text">
              <span className="alert-title" data-notify="title">
                Erro
              </span>
              <span data-notify="message">
                Não foi possível carregar o histórico de atualizações
              </span>
            </div>
          ),
          type: "danger",
          icon: "ni ni-bell-55",
          autoDismiss: 7,
        };
        notificationAlertRef.current.notificationAlert(options);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUserUpdateHistory();
  }, []);

  const handleSearch = async (overrideDate, overrideAction) => {
    const filterDate   = overrideDate   !== undefined ? overrideDate   : dateFilter;
    const filterAction = overrideAction !== undefined ? overrideAction : actionFilter;

    if (!search && !filterDate && !filterAction) {
      setIsSearching(false);
      return loadUserUpdateHistory();
    }

    setSearchLoading(true);
    setIsSearching(true);
    setPage(1);

    console.log("Filtro de data aplicado:", filterDate);

    try {
      const searchTerms = search.toLowerCase().trim().split(/\s+/).filter(t => t);
      const filtered = logs.filter(log => {
        const logDate = moment(log.updated_at).format("YYYY-MM-DD");
        const dateMatch = !filterDate ? true : logDate === filterDate;
        const actionMatch = !filterAction ? true : log.action === filterAction;
        if (!dateMatch || !actionMatch) return false; 

        if (!searchTerms.length) return true;

        return searchTerms.every(term => {
          const all = [
            `${log.editor_name} ${log.editor_lastname}`.toLowerCase(),
            `${log.user_name} ${log.user_lastname}`.toLowerCase(),
            log.change_description.toLowerCase(),
            (log.user_id || "").toString()
          ].join(" ");
          return all.includes(term);
        });
      });

      console.log(`Encontrados ${filtered.length} registros após filtro`);
      setFilteredLogs(filtered);
      setTotalPages(Math.ceil(filtered.length / logsPerPage));
    } catch (err) {
      console.error(err);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleDateFilterChange = (e) => {
    const selectedDate = e.target.value;
    console.log("Data digitada:", selectedDate);

    if (!selectedDate) {
      setDateFilter("");
      setIsSearching(false);
      return loadUserUpdateHistory();
    }

    const parsed = moment(selectedDate, ["DD/MM/YYYY", "YYYY-MM-DD"], true);
    if (!parsed.isValid()) {
      setDateFilter("");
      setIsSearching(false);
      return loadUserUpdateHistory();
    }

    const formattedDate = parsed.format("YYYY-MM-DD");
    setDateFilter(formattedDate);

    handleSearch(formattedDate);
  };

  const clearSearch = () => {
    setLoading(true);
    setSearch("");
    setDateFilter("");
    setActionFilter("");
    setPage(1);
    setIsSearching(false);
    
    setTimeout(() => {
      loadUserUpdateHistory().finally(() => {
        setLoading(false);
      });
    }, 10);
  };

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
  };

  const openLogDetails = (log) => {
    setSelectedLog(log);
    setShowDetailsModal(true);
  };

  const closeLogDetails = () => {
    setShowDetailsModal(false);
    setSelectedLog(null);
  };

  // Calcular logs paginados
  const paginatedLogs = isSearching
    ? filteredLogs.slice((page - 1) * logsPerPage, page * logsPerPage)
    : logs.slice((page - 1) * logsPerPage, page * logsPerPage);

  const filteredTotalPages = Math.ceil(filteredLogs.length / logsPerPage);

  return (
    <>
      <div className="rna-wrapper">
        <NotificationAlert ref={notificationAlertRef} />
      </div>
      
      <SimpleHeader 
        name="Histórico de Usuários" 
        way={[{name: "Dashboard", url: "/dashboard"}, {name: "Histórico de Atualizações", url: "/system-logs"}]} 
      />

      <Container className="mt--6" fluid>
        <Card className="shadow">
          <CardHeader className="border-0">
            <Row>
              <Col xs="6">
                <h3 className="mb-0">Histórico de Usuários</h3>
              </Col>
              <Col className="text-right" xs="6">
                {loading && <ClipLoader size={20} color={"#5e72e4"} loading={loading} />}
              </Col>
            </Row>
          </CardHeader>

          <CardBody>
            <Card className="shadow mb-4">
              <CardHeader className="border-0">
                <h6 className="mb-0">Filtros</h6>
              </CardHeader>
              <CardBody>
                <Row>
                  <Col md="4">
                    <FormGroup>
                      <Label for="searchInput">Pesquisar por Nome, Editor ou Descrição</Label>
                      <InputGroup>
                        <Input
                          id="searchInput"
                          type="text"
                          placeholder="Digite para pesquisar..."
                          value={search}
                          onChange={handleSearchChange}
                          onKeyPress={e => e.key === 'Enter' && handleSearch()}
                        />
                        <InputGroupAddon addonType="append">
                          <Button color="primary" onClick={handleSearch}>
                            {searchLoading
                              ? <ClipLoader size={15} color="#fff" loading />
                              : <i className="fas fa-search" />
                            }
                          </Button>
                        </InputGroupAddon>
                      </InputGroup>
                    </FormGroup>
                  </Col>
                  <Col md="4">
                    <FormGroup>
                      <Label for="dateFilter">Filtrar por Data</Label>
                      <div className="d-flex align-items-center">
                        <div className="flex-grow-1">
                          {dateInputType === "select" ? (
                            <Input
                              id="dateFilter"
                              type="select"
                              value={dateFilter}
                              onChange={handleDateFilterChange}
                            >
                              <option value="">Todas as datas</option>
                              {uniqueDates.map(date => (
                                <option key={date} value={date}>
                                  {moment(date).format('DD/MM/YYYY')}
                                </option>
                              ))}
                            </Input>
                          ) : (
                            <Input
                              id="dateFilterManual"
                              type="date"
                              value={dateFilter}
                              onChange={handleDateFilterChange}
                              placeholder="YYYY-MM-DD"
                            />
                          )}
                        </div>
                        <Button
                          color="link"
                          className="ml-2"
                          onClick={() => setDateInputType(dateInputType === "select" ? "manual" : "select")}
                        >
                          {dateInputType === "select" ? (
                            <span title="Digitar data manualmente">
                              <i className="fas fa-keyboard"></i>
                            </span>
                          ) : (
                            <span title="Selecionar da lista">
                              <i className="fas fa-list"></i>
                            </span>
                          )}
                        </Button>
                      </div>
                      {dateInputType === "manual" && (
                        <small className="form-text text-muted">
                          Digite a data no formato DD-MM-AAAA ou use o seletor de data
                        </small>
                      )}
                    </FormGroup>
                  </Col>
                  <Col md="4">
                    <FormGroup>
                      <Label for="actionFilter">Filtrar por Ação</Label>
                      <Input
                        id="actionFilter"
                        type="select"
                        value={actionFilter}
                        onChange={e => {
                          const val = e.target.value;
                          setActionFilter(val);
                          handleSearch(undefined, val);
                        }}
                      >
                        <option value="">Todas as ações</option>
                        <option value="create">Criação</option>
                        <option value="update">Edição</option>
                        <option value="delete">Remoção</option>
                      </Input>
                    </FormGroup>
                  </Col>
                </Row>
              </CardBody>
            </Card>

            {searchLoading ? (
              <div className="text-center py-5">
                <ClipLoader size={50} color={"#5e72e4"} loading={searchLoading} />
                <p className="mt-3 lead">Buscando no histórico...</p>
                <p className="text-muted">Isso pode levar alguns segundos</p>
              </div>
            ) : paginatedLogs.length > 0 ? (
              <div className="table-responsive">
                <Table className="align-items-center table-flush">
                  <thead className="thead-light">
                    <tr>
                      <th>Data e Hora</th>
                      <th>Usuário</th>
                      <th>Editor</th>
                      <th>Ação</th>
                      <th>Descrição da Alteração</th>
                      <th>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedLogs.map((item, index) => {
                      // normalize action label
                      const actionLabel = {
                        create:  "Criação",
                        update:  "Edição",
                        delete:  "Remoção"
                      }[item.action] || item.action;
                      const actionColor = {
                        create:  "success",
                        update:  "warning",
                        delete:  "danger"
                      }[item.action] || "secondary";

                      return (
                        <tr key={`log-${index}`}>
                          <td>{moment(item.updated_at).format('DD/MM/YYYY HH:mm:ss')}</td>
                          <td>
                            {item.user_name
                              ? `${item.user_name} ${item.user_lastname} (#${item.user_id})`
                              : `#${item.user_id}`}
                          </td>
                          <td>
                            {item.editor_name} {item.editor_lastname}
                          </td>
                          <td>
                            <Badge color={actionColor} pill>
                              {actionLabel}
                            </Badge>
                          </td>
                          <td>
                            <div
                              onClick={() => openLogDetails(item)}
                              title="Clique para ver detalhes completos"
                              style={{
                                maxWidth: "300px",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                cursor: "pointer",
                                textDecoration: "underline dotted",
                                color: "#3498db"
                              }}
                            >
                              {item.change_description}
                            </div>
                          </td>
                          <td>
                            <Button
                              color="info"
                              size="sm"
                              className="btn-icon btn-sm"
                              onClick={() => openLogDetails(item)}
                              id={`tooltip-${index}`}
                            >
                              <i className="fas fa-eye"></i>
                            </Button>
                            <UncontrolledTooltip delay={0} target={`tooltip-${index}`}>
                              Ver detalhes completos
                            </UncontrolledTooltip>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>

                <div className="d-flex justify-content-between align-items-center mt-4">
                  <small className="text-muted">
                    Mostrando {paginatedLogs.length === 0 ? 0 : (page - 1) * logsPerPage + 1} a{' '}
                    {Math.min(page * logsPerPage, isSearching ? filteredLogs.length : totalLogs)} de{' '}
                    {isSearching ? filteredLogs.length : totalLogs} registros
                  </small>

                  <div className="flex-fill text-center">
                    {isSearching && (
                      <Button outline color="primary" onClick={clearSearch}>
                        Limpar pesquisa
                      </Button>
                    )}
                  </div>

                  <Pagination>
                    <PaginationItem disabled={page === 1}>
                      <PaginationLink onClick={() => setPage(page - 1)}>
                        <i className="fas fa-angle-left" /><span className="sr-only">Anterior</span>
                      </PaginationLink>
                    </PaginationItem>

                    {getPageNumbers().map((p, idx) => {
                      if (p === 'start-ellipsis' || p === 'end-ellipsis') {
                        return (
                          <PaginationItem key={p + idx} disabled>
                            <PaginationLink>…</PaginationLink>
                          </PaginationItem>
                        );
                      }
                      return (
                        <PaginationItem key={p} active={p === page}>
                          <PaginationLink onClick={() => setPage(p)}>
                            {p}
                          </PaginationLink>
                        </PaginationItem>
                      );
                    })}

                    <PaginationItem disabled={page === (isSearching ? filteredTotalPages : totalPages)}>
                      <PaginationLink onClick={() => setPage(page + 1)}>
                        <i className="fas fa-angle-right" /><span className="sr-only">Próxima</span>
                      </PaginationLink>
                    </PaginationItem>
                  </Pagination>
                </div>
              </div>
            ) : (
              <div className="text-center py-5">
                <p className="lead">Nenhum registro de edição encontrado.</p>
                {isSearching && (
                  <Button color="primary" outline onClick={clearSearch} disabled={loading}>
                    {loading ? (
                      <>
                        <ClipLoader size={15} color={"#5e72e4"} loading={true} className="mr-2" />
                        Limpando...
                      </>
                    ) : (
                      "Limpar pesquisa"
                    )}
                  </Button>
                )}
              </div>
            )}
          </CardBody>
        </Card>
      </Container>

      {/* Modal de detalhes do log */}
      {showDetailsModal && selectedLog && (
        <LogDetails 
          isOpen={showDetailsModal} 
          toggle={closeLogDetails} 
          log={{
            ...selectedLog,
            createDate:    selectedLog.updated_at,
            userName:      `${selectedLog.editor_name} ${selectedLog.editor_lastname}`,
            user:          selectedLog.updated_by_user_id,
            url:           `${selectedLog.action.toUpperCase()} do usuário #${selectedLog.user_id}`,
            method:        selectedLog.action === 'delete' ? 'DELETE' : 'PUT',
            data:          JSON.parse(selectedLog.change_details || '{}'),
            description:   selectedLog.change_description,
            action:        selectedLog.action
          }}
          getActionDescription={() => selectedLog.change_description}
          getActionCategory={() => {
            switch (selectedLog.action) {
              case 'create': return 'Criação';
              case 'update': return 'Edição';
              case 'delete': return 'Remoção';
              default:       return selectedLog.action;
            }
          }}
          getActionIcon={() => {
            switch (selectedLog.action) {
              case 'create': return 'fas fa-plus';
              case 'update': return 'fas fa-user-edit';
              case 'delete': return 'fas fa-trash';
              default:       return 'fas fa-info-circle';
            }
          }}
        />
      )}
    </>
  );
};

export default connect(state => ({
  translate: state.translate,
  permission: state.permission,
}))(SystemLogs);